import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { MultiSelect } from 'primeng/multiselect';
import { Select } from 'primeng/select';
import { InputNumber } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';
import { CarePlanEditorShellComponent } from '../../shared/care-plan-editor-shell/care-plan-editor-shell.component';
import { CarePlanEditorRowComponent } from '../../shared/care-plan-editor-row/care-plan-editor-row.component';
import { caseApiErrorMessage } from '../../cases/api/case-api-error';
import { OutcomeCard } from '../api/outcome-api.model';
import { OutcomesStore } from '../outcomes.store';
import {
  OutcomeAnalytics,
  OutcomeEvent,
  OutcomeEventDetails,
  outcomeErrorType,
} from '../../analytics/outcome-analytics';
import { CARE_PLAN_NAVI } from '../../care-plan-navi.token';

export interface OutcomeEditorDialogData {
  store: OutcomesStore;
  outcome?: OutcomeCard;
}

@Component({
  selector: 'bn-outcome-editor-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    InputNumber,
    DatePipe,
    FormsModule,
    Button,
    ConfirmDialog,
    MultiSelect,
    Select,
    Textarea,
    CarePlanEditorShellComponent,
    CarePlanEditorRowComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './outcome-editor-dialog.component.html',
  styleUrl: './outcome-editor-dialog.component.scss',
})
export class OutcomeEditorDialogComponent {
  private readonly analytics = inject(OutcomeAnalytics);
  private readonly messages = inject(MessageService, { optional: true });
  private readonly openedAt = Date.now();
  private closed = false;
  private readonly navi = inject(CARE_PLAN_NAVI, { optional: true });
  private readonly ref = inject(DynamicDialogRef);
  private readonly confirmation = inject(ConfirmationService);
  private readonly data = inject(DynamicDialogConfig)
    .data as OutcomeEditorDialogData;
  readonly store = this.data.store;
  constructor() {
    this.track('outcome_modal_opened');
    inject(DestroyRef).onDestroy(() => {
      if (!this.closed)
        this.track('outcome_modal_closed', { reason: 'dismissed' });
      this.closed = true;
      if (this.store.session() === this.session) this.store.clearMoveDraft();
    });
  }
  readonly outcome = this.data.outcome;
  private readonly session = this.store.session();
  readonly clientScoped = this.session?.caseId === null;
  private readonly originalCaseId = this.session?.caseId ?? null;
  readonly selectedCaseId = signal<number | null>(this.originalCaseId);
  readonly caseInfoOpen = signal(false);
  readonly selectedCaseInfo = computed(() => {
    const session = this.store.session();
    const caseId = this.selectedCaseId();
    if (session !== this.session || caseId === null) return null;
    const carePlan = session?.carePlan;
    if (!carePlan) return null;
    const selected = carePlan
      .cases()
      .find((item) => item.id === String(caseId));
    if (!selected) return null;
    const domain = carePlan
      .domains()
      .find(
        (item) =>
          item.domainId === selected.domainId ||
          item.code === selected.domain ||
          `domain-id:${item.domainId}` === selected.domain,
      );
    return {
      domain: domain?.shortName || domain?.name || '—',
      status: selected.status ? carePlan.statusLabel(selected.status) : '—',
      openedAt:
        selected.createdAt && Number.isFinite(Date.parse(selected.createdAt))
          ? selected.createdAt
          : null,
    };
  });
  readonly unlinking = computed(
    () =>
      !!this.outcome &&
      this.originalCaseId !== null &&
      this.selectedCaseId() === null,
  );
  readonly reassigning = computed(
    () =>
      this.originalCaseId !== null &&
      this.selectedCaseId() !== null &&
      this.selectedCaseId() !== this.originalCaseId,
  );
  readonly caseChoices = computed(() => {
    const current = this.store.currentCaseChoice();
    return [
      { label: 'No case', value: null, disabled: false },
      ...(current ? [{ ...current, disabled: false }] : []),
      ...this.store
        .caseChoices()
        .filter((item) => item.value !== current?.value)
        .map((item) => ({ ...item, disabled: false })),
    ];
  });
  readonly caseError = computed(() =>
    this.selectedCaseId() !== null &&
    this.selectedCaseId() !== this.originalCaseId &&
    !this.store
      .caseChoices()
      .some((item) => item.value === this.selectedCaseId())
      ? 'This case is no longer available. Select another case.'
      : null,
  );
  async selectCase(caseId: number | null): Promise<void> {
    if (!this.outcome || this.saving() || !this.writable()) return;
    if (this.store.movePending()) return;
    this.selectedCaseId.set(caseId);
    if (caseId === null) this.caseInfoOpen.set(false);
    this.linkedOptions.set(
      caseId === this.originalCaseId
        ? this.outcome.linkedOptions.map((option) => option.linkedOptionId)
        : [],
    );
    await this.store.loadCaseOptions(caseId);
  }
  readonly name = signal(this.outcome?.name ?? '');
  readonly description = signal(this.outcome?.description ?? '');
  readonly descriptionError = computed(() =>
    this.description().length > 500
      ? 'Keep the description to 500 characters or fewer.'
      : null,
  );
  readonly typeCode = signal<string | null>(
    this.outcome?.outcomeTypeCode ?? null,
  );
  readonly linkedOptions = signal<number[]>(
    this.outcome?.linkedOptions.map((option) => option.linkedOptionId) ?? [],
  );
  readonly ati = signal(
    this.outcome?.atiValue == null ? '' : String(this.outcome.atiValue),
  );
  readonly atiAmount = computed(() =>
    this.ati().trim() ? Number(this.ati()) : null,
  );
  readonly reason = signal('');
  readonly removing = signal(false);
  readonly attempted = signal(false);
  readonly removalAttempted = signal(false);
  readonly saving = signal(false);
  readonly saveError = signal<string | null>(null);
  readonly writable = computed(
    () =>
      this.store.session() === this.session &&
      (this.outcome ? this.store.canEdit(this.outcome) : this.store.canWrite()),
  );
  readonly types = computed(() => [...this.store.types()]);
  readonly optionChoices = computed(() => {
    const choices = this.store.options().map((option) => ({
      id: Number(option.id),
      label: option.title,
      disabled: false,
    }));
    for (const option of this.selectedCaseId() === this.originalCaseId
      ? (this.outcome?.linkedOptions ?? [])
      : []) {
      if (!choices.some((choice) => choice.id === option.linkedOptionId)) {
        choices.push({
          id: option.linkedOptionId,
          label: `${option.linkedOptionLabel} (unavailable)`,
          disabled: !this.linkedOptions().includes(option.linkedOptionId),
        });
      }
    }
    return choices;
  });
  readonly nameError = computed(() => {
    const value = this.name().trim();
    return !value || value.length > 250
      ? 'Enter a name between 1 and 250 characters.'
      : null;
  });
  readonly typeError = computed(() =>
    this.types().some((type) => type.code === this.typeCode())
      ? null
      : 'Select an outcome type.',
  );
  readonly optionsError = computed(() => {
    if (this.outcome && this.selectedCaseId() === null) return null;
    const ids = this.linkedOptions();
    return ids.length > 0 &&
      ids.every((id) => this.optionChoices().some((choice) => choice.id === id))
      ? null
      : 'Select at least one linked option.';
  });
  readonly atiError = computed(() => {
    if (this.clientScoped) return null;
    const value = this.ati().trim();
    if (!value) return null;
    return /^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(value) &&
      Number(value) <= 99999999.99
      ? null
      : 'Enter an amount from 0 to 99,999,999.99 with up to two decimal places.';
  });
  readonly reasonError = computed(() => {
    const length = this.reason().trim().length;
    return length >= 10 && length <= 500
      ? null
      : 'Enter a reason between 10 and 500 characters.';
  });
  readonly contentDirty = computed(
    () =>
      this.name() !== (this.outcome?.name ?? '') ||
      this.description() !== (this.outcome?.description ?? '') ||
      this.typeCode() !== (this.outcome?.outcomeTypeCode ?? null) ||
      (!this.clientScoped &&
        this.ati() !==
          (this.outcome?.atiValue == null
            ? ''
            : String(this.outcome.atiValue))),
  );

  readonly linkOnly = computed(
    () =>
      this.clientScoped &&
      this.selectedCaseId() !== null &&
      !this.contentDirty(),
  );
  readonly dirty = computed(
    () =>
      this.contentDirty() ||
      JSON.stringify([...this.linkedOptions()].sort()) !==
        JSON.stringify(
          (
            this.outcome?.linkedOptions.map(
              (option) => option.linkedOptionId,
            ) ?? []
          ).sort(),
        ) ||
      !!this.reason() ||
      this.selectedCaseId() !== this.originalCaseId,
  );

  readonly invalidFields = computed(() => {
    const typeCode = this.typeCode();
    const checkContent =
      !this.linkOnly() && !(this.reassigning() && !this.contentDirty());
    return [
      ...(checkContent && this.nameError() ? ['name'] : []),
      ...(checkContent && this.descriptionError() ? ['description'] : []),
      ...(!typeCode || (checkContent && this.typeError())
        ? ['outcome_type']
        : []),
      ...(this.optionsError() ? ['linked_options'] : []),
      ...((this.clientScoped || this.reassigning()) && this.caseError()
        ? ['case']
        : []),
      ...(this.outcome && !this.linkOnly() && this.atiError() ? ['ati'] : []),
    ];
  });
  readonly choicesUnavailable = computed(
    () =>
      !!(
        this.store.referenceLoading() ||
        this.store.referenceError() ||
        (this.selectedCaseId() !== null &&
          (this.store.linkOptionsLoading() || this.store.linkOptionsError()))
      ),
  );
  readonly noEligibleCases = computed(() => {
    const carePlan = this.store.session()?.carePlan;
    return (
      !!this.outcome &&
      this.writable() &&
      !this.store.referenceLoading() &&
      !this.store.referenceError() &&
      !carePlan?.loading?.() &&
      !carePlan?.error?.() &&
      this.store.caseChoices().length === 0
    );
  });
  readonly canSubmit = computed(() => {
    if (this.saving() || !this.writable()) return false;
    if (this.unlinking()) return !this.contentDirty();
    return !this.choicesUnavailable() && this.invalidFields().length === 0;
  });

  private track(event: OutcomeEvent, details: OutcomeEventDetails = {}): void {
    if (!this.session) return;
    this.analytics.log(event, this.session, {
      mode: this.outcome ? 'edit' : 'add',
      ...(this.outcome
        ? {
            outcome_id: String(this.outcome.outcomeId),
            outcome_type: this.outcome.outcomeTypeCode,
          }
        : {}),
      ...details,
    });
  }

  private confirmSuccess(summary: string): void {
    if (this.closed || this.store.session() !== this.session) return;
    try {
      this.messages?.add({ severity: 'success', summary, life: 4000 });
    } catch {
      // Notification failure must not make a committed change retryable.
    }
  }

  private close(
    result: unknown,
    reason: 'saved' | 'canceled' | 'discarded',
  ): void {
    if (this.closed) return;
    this.closed = true;
    this.track('outcome_modal_closed', { reason });
    this.ref.close(result);
  }

  private changedFields(): string[] {
    const original = this.outcome;
    if (!original) return [];
    const fields = {
      name: this.name().trim() !== (original.name ?? '').trim(),
      description:
        this.description().trim() !== (original.description ?? '').trim(),
      outcome_type: this.typeCode() !== original.outcomeTypeCode,
      linked_options:
        this.selectedCaseId() === this.originalCaseId &&
        JSON.stringify([...this.linkedOptions()].sort()) !==
          JSON.stringify(
            original.linkedOptions
              .map((option) => option.linkedOptionId)
              .sort(),
          ),
      ati:
        !this.clientScoped &&
        (this.ati().trim() ? Number(this.ati()) : null) !== original.atiValue,
    };
    return Object.entries(fields)
      .filter(([, changed]) => changed)
      .map(([field]) => field);
  }

  private failure(
    error: unknown,
    operation: NonNullable<OutcomeEventDetails['operation']>,
  ): void {
    const progress = operation === 'relink' ? this.store.moveProgress() : null;
    this.track('outcome_save_failed', {
      operation,
      error_type: outcomeErrorType(error),
      ...(progress
        ? { stage: progress.stage, source_unlinked: progress.sourceUnlinked }
        : {}),
    });
  }

  async submit(): Promise<void> {
    if (this.closed || this.saving() || !this.writable()) return;
    this.attempted.set(true);
    if (this.unlinking()) {
      await this.submitUnlink();
      return;
    }
    if (this.choicesUnavailable()) return;
    const typeCode = this.typeCode();
    const invalidFields = this.invalidFields();
    if (invalidFields.length) {
      this.track('outcome_validation_failed', {
        invalid_fields: invalidFields,
      });
      return;
    }
    if (!typeCode) return;
    this.saving.set(true);
    this.saveError.set(null);
    const operation = !this.outcome
      ? 'calculate'
      : this.reassigning()
        ? 'relink'
        : this.clientScoped && this.selectedCaseId() !== null
          ? 'link'
          : 'update';
    const changedFields = this.changedFields();
    const targetCaseId = this.selectedCaseId();
    const targetCaseLabel =
      this.caseChoices().find((choice) => choice.value === targetCaseId)
        ?.label ?? `Case #${targetCaseId}`;
    const caseStatus = this.session?.carePlan
      ?.cases()
      .find((item) => item.id === String(targetCaseId))?.status;
    try {
      if (!this.outcome) {
        const session = this.session;
        if (!session?.caseId || !this.writable()) return;
        const type = this.types().find((value) => value.code === typeCode);
        const options = this.linkedOptions().map((id) => {
          const option = this.optionChoices().find((value) => value.id === id);
          return `Linked option: ${option?.label} (Option ID: ${id})`;
        });
        const prompt = [
          `Calculate ATI for this outcome on Case ID ${session.caseId}.`,
          '',
          `Outcome: “${this.name().trim()}”`,
          ...(this.description().trim()
            ? [`Description: ${this.description().trim()}`]
            : []),
          `Type: ${type?.label}`,
          ...options,
        ].join('\n');
        const sent = await this.navi?.sendMessageToSelectedConversation(
          prompt,
          session.guid,
        );
        if (!sent) {
          throw new Error(
            'Navi is unavailable for this conversation. Try again when the chat is ready.',
          );
        }
        this.track('outcome_calculation_requested', {
          outcome_type: typeCode,
          option_ids: this.linkedOptions().map(String),
        });
        this.close({ sentToNavi: true }, 'saved');
        return;
      }
      const value = {
        name: this.name().trim(),
        description: this.description().trim(),
        outcomeTypeCode: typeCode,
        linkedOptions: [...this.linkedOptions()],
        ...(!this.clientScoped
          ? { atiValue: this.ati().trim() ? Number(this.ati()) : null }
          : {}),
      };
      if (this.reassigning()) {
        const caseId = this.selectedCaseId();
        if (caseId === null) return;
        await this.store.reassign(
          this.outcome,
          caseId,
          [...this.linkedOptions()],
          this.contentDirty() ? value : undefined,
        );
      } else if (this.linkOnly()) {
        const caseId = this.selectedCaseId();
        if (caseId === null) return;
        await this.store.link(this.outcome, caseId, [...this.linkedOptions()]);
      } else if (this.clientScoped) {
        await this.store.save(value, this.outcome, this.selectedCaseId());
      } else {
        await this.store.save(value, this.outcome);
      }
      if (operation === 'relink') {
        this.track('outcome_relinked', {
          outcome_type: typeCode,
          from_case_id: String(this.originalCaseId),
          to_case_id: String(targetCaseId),
          from_option_ids: this.outcome.linkedOptions.map((option) =>
            String(option.linkedOptionId),
          ),
          to_option_ids: this.linkedOptions().map(String),
          seconds_from_open: Math.max(0, (Date.now() - this.openedAt) / 1000),
        });
      } else if (operation === 'link') {
        this.track('outcome_link_saved', {
          outcome_type: typeCode,
          case_id: String(targetCaseId),
          ...(caseStatus ? { case_status: caseStatus } : {}),
          option_ids: this.linkedOptions().map(String),
          seconds_from_open: Math.max(0, (Date.now() - this.openedAt) / 1000),
        });
      }
      if (changedFields.length)
        this.track('outcome_updated', {
          outcome_type: typeCode,
          changed_fields: changedFields,
        });
      if (operation === 'link' || operation === 'relink')
        this.confirmSuccess(`Linked to ${targetCaseLabel}`);
      this.close({ saved: true }, 'saved');
    } catch (error) {
      this.failure(error, operation);
      this.saveError.set(
        caseApiErrorMessage(
          error,
          this.outcome
            ? 'The outcome could not be saved. Please try again.'
            : 'The outcome could not be sent to Navi. Please try again.',
        ),
      );
    } finally {
      this.saving.set(false);
    }
  }

  private async submitUnlink(): Promise<void> {
    if (this.contentDirty() && this.writable() && !this.saving())
      this.track('outcome_validation_failed', {
        invalid_fields: ['content_changes'],
      });
    if (
      !this.outcome ||
      !this.writable() ||
      this.saving() ||
      this.contentDirty()
    )
      return;
    this.saving.set(true);
    this.saveError.set(null);
    try {
      await this.store.unlink(this.outcome);
      this.track('outcome_unlinked', {
        from_case_id: String(this.originalCaseId),
        from_option_ids: this.outcome.linkedOptions.map((option) =>
          String(option.linkedOptionId),
        ),
      });
      this.confirmSuccess('Outcome unlinked');
      this.close({ unlinked: true }, 'saved');
    } catch (error) {
      this.failure(error, 'unlink');
      this.saveError.set(
        caseApiErrorMessage(
          error,
          'The outcome could not be unlinked. Your selection is still here. Please try again.',
        ),
      );
    } finally {
      this.saving.set(false);
    }
  }

  startRemoval(): void {
    if (
      this.store.movePending() ||
      this.clientScoped ||
      !this.outcome ||
      !this.writable() ||
      this.saving()
    )
      return;
    this.removing.set(true);
    this.saveError.set(null);
  }

  async confirmRemoval(): Promise<void> {
    if (this.closed) return;
    this.removalAttempted.set(true);
    if (
      !this.clientScoped &&
      !this.store.movePending() &&
      this.writable() &&
      !this.saving() &&
      this.reasonError()
    )
      this.track('outcome_validation_failed', {
        invalid_fields: ['removal_reason'],
      });
    if (
      this.store.movePending() ||
      this.clientScoped ||
      !this.outcome ||
      !this.writable() ||
      this.saving() ||
      this.reasonError()
    )
      return;
    this.saving.set(true);
    this.saveError.set(null);
    try {
      await this.store.invalidate(this.outcome, this.reason().trim());
      this.track('outcome_removed');
      this.close({ removed: true }, 'saved');
    } catch (error) {
      this.failure(error, 'remove');
      this.saveError.set(
        caseApiErrorMessage(
          error,
          'The outcome could not be removed. Please try again.',
        ),
      );
    } finally {
      this.saving.set(false);
    }
  }

  back(): void {
    if (this.saving()) return;
    this.removing.set(false);
    this.saveError.set(null);
  }

  tryClose(): void {
    if (this.closed || this.saving()) return;
    if (!this.dirty()) {
      this.close(undefined, 'canceled');
      return;
    }
    this.confirmation.confirm({
      header: 'Unsaved changes',
      message: 'Discard your unsaved outcome changes?',
      acceptLabel: 'Discard',
      rejectLabel: 'Stay',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.close(undefined, 'discarded'),
    });
  }
}
